import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { FormsModule } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SharedModule } from "src/shared/shared.module";
import { MANUAL_MODULES, ManualModuleDef, ManualStepDef } from "./manual.data";
import { MANUAL_SHOT_MAP, ManualHotspot } from "./manual.hotspots";

/** A step with its measured markers resolved and numbered for display. */
export interface ResolvedStep {
  def: ManualStepDef;
  image: string;
  marks: (ManualHotspot & { index: number })[];
}

export interface ResolvedModule {
  def: ManualModuleDef;
  steps: ResolvedStep[];
}

/**
 * The user manual: one page, every module, read it or watch it.
 *
 * Two decisions shape this screen.
 *
 * First, ONE page rather than a section per feature. Someone looking for "how
 * do I see what I'm weak at" does not know whether that lives under quizzes or
 * under the profile, and a manual split across eight routes makes them guess.
 * Collapsed modules keep the whole map visible; the search box reaches into the
 * step text, so a word they remember finds the screen it is on.
 *
 * Second, the screenshots are REAL and their markers are MEASURED — captured
 * from the running app by test/tools/manual-capture.mjs. Nothing here is a
 * drawing that quietly stops matching the product; when the UI moves, the
 * capture is re-run and the numbers move with it. Markers the capture could not
 * find (controls that only exist for some accounts) simply do not render, and
 * the numbering closes up rather than pointing at empty space.
 */
@Component({
  selector: "app-manual",
  imports: [CommonModule, RouterModule, FormsModule, TranslateModule, SharedModule],
  templateUrl: "./manual.component.html",
  styleUrl: "./manual.component.scss",
})
export class ManualComponent {
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #translate = inject(TranslateService);

  readonly modules = MANUAL_MODULES;

  mode = signal<"read" | "video">("read");
  query = signal("");
  /** Which modules are expanded. The first one starts open so the page is not a wall of closed bars. */
  openIds = signal<Set<string>>(new Set([MANUAL_MODULES[0].id]));
  /** The marker the pointer is on, as `${stepKey}:${markKey}`, for the highlight. */
  activeMark = signal<string | null>(null);
  /** Bumped on language change so the search filter re-runs against the new copy. */
  #lang = signal(this.#translate.currentLang);

  constructor() {
    this.#translate.onLangChange.subscribe((e) => this.#lang.set(e.lang));

    // Mode comes from the route (`/manual` vs `/manual/video`) so the two menu
    // entries are real, linkable destinations rather than a toggle the reader
    // has to find and press.
    this.mode.set(this.#route.snapshot.data["mode"] === "video" ? "video" : "read");

    // `/manual#quiz` opens that module directly — what a support reply links to.
    const fragment = this.#route.snapshot.fragment;
    if (fragment) {
      const target = MANUAL_MODULES.find((m) => m.id.toLowerCase() === fragment.toLowerCase());
      if (target) {
        this.openIds.set(new Set([target.id]));
        setTimeout(() => this.scrollTo(target.id), 300);
      }
    }
  }

  /** Modules with their markers resolved, filtered by the search box. */
  resolved = computed<ResolvedModule[]>(() => {
    const needle = this.query().trim().toLowerCase();
    this.#lang(); // re-run when the language changes

    return this.modules
      .map((def) => ({
        def,
        steps: def.steps.map((step) => this.#resolveStep(step)).filter((s) => !!s.image),
      }))
      .map((mod) => ({
        ...mod,
        steps: needle ? mod.steps.filter((s) => this.#matches(mod.def, s, needle)) : mod.steps,
      }))
      .filter((mod) => !needle || mod.steps.length || this.#moduleMatches(mod.def, needle));
  });

  #resolveStep(step: ManualStepDef): ResolvedStep {
    const shot = MANUAL_SHOT_MAP[step.shot];
    if (!shot) return { def: step, image: "", marks: [] };

    // Reading order comes from the data file; anything the capture could not
    // measure drops out here, which is why the numbers are assigned after the
    // filter rather than from the declared position.
    const marks = step.marks
      .map((key) => shot.hotspots.find((h) => h.key === key))
      .filter((h): h is ManualHotspot => !!h)
      .map((h, i) => ({ ...h, index: i + 1 }));

    return { def: step, image: `assets/manual/img/${shot.image}`, marks };
  }

  #matches(mod: ManualModuleDef, step: ResolvedStep, needle: string): boolean {
    const parts = [
      this.stepTitle(mod, step.def),
      this.stepBody(mod, step.def),
      ...step.marks.map((m) => this.markText(mod, step.def, m.key)),
      this.moduleTitle(mod),
    ];
    return parts.join(" ").toLowerCase().includes(needle);
  }

  #moduleMatches(mod: ManualModuleDef, needle: string): boolean {
    return `${this.moduleTitle(mod)} ${this.moduleDesc(mod)}`.toLowerCase().includes(needle);
  }

  /* ------------------------------------------------------------ i18n paths */

  moduleTitle = (m: ManualModuleDef) => this.#t(`MANUAL.M.${m.id}.TITLE`);
  moduleDesc = (m: ManualModuleDef) => this.#t(`MANUAL.M.${m.id}.DESC`);
  moduleVideoNote = (m: ManualModuleDef) => this.#t(`MANUAL.M.${m.id}.VIDEO`);
  stepTitle = (m: ManualModuleDef, s: ManualStepDef) => this.#t(`MANUAL.M.${m.id}.S.${s.id}.TITLE`);
  stepBody = (m: ManualModuleDef, s: ManualStepDef) => this.#t(`MANUAL.M.${m.id}.S.${s.id}.BODY`);
  markText = (m: ManualModuleDef, s: ManualStepDef, key: string) =>
    this.#t(`MANUAL.M.${m.id}.S.${s.id}.MARKS.${key}`);

  #t(key: string): string {
    const value = this.#translate.instant(key);
    // ngx-translate echoes the key back when it is missing; showing a raw key
    // in a manual is worse than showing nothing.
    return value === key ? "" : value;
  }

  /* --------------------------------------------------------------- actions */

  isOpen = (id: string) => this.openIds().has(id);

  toggle(id: string): void {
    const next = new Set(this.openIds());
    next.has(id) ? next.delete(id) : next.add(id);
    this.openIds.set(next);
  }

  expandAll(): void {
    this.openIds.set(new Set(this.modules.map((m) => m.id)));
  }

  collapseAll(): void {
    this.openIds.set(new Set());
  }

  setMode(mode: "read" | "video"): void {
    this.mode.set(mode);
    this.#router.navigate([mode === "video" ? "/manual/video" : "/manual"], {
      replaceUrl: true,
    });
  }

  openFrom(id: string): void {
    const next = new Set(this.openIds());
    next.add(id);
    this.openIds.set(next);
    setTimeout(() => this.scrollTo(id), 120);
  }

  scrollTo(id: string): void {
    document.getElementById(`manual-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  poster(mod: ResolvedModule): string {
    return mod.steps[0]?.image ?? "";
  }

  video(mod: ManualModuleDef): string {
    return `assets/manual/video/${mod.video}.mp4`;
  }

  stepKey(mod: ManualModuleDef, step: ResolvedStep): string {
    return `${mod.id}.${step.def.id}`;
  }

  totalSteps = computed(() =>
    this.resolved().reduce((sum, mod) => sum + mod.steps.length, 0)
  );
}
